import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { aiAgentApi } from '../../lib/aiAgentApi';

const POLL_MS = 25000;

/**
 * Live vendor-reply notifications.
 *
 * Polls /ai-agent/email/reply-events and, the moment a vendor reply lands,
 * fires a sonner toast (with an "Open inbox" action) plus a native desktop
 * notification when the user has granted permission. Session-scoped: only
 * replies received AFTER the hook mounts are announced, so we never replay
 * a backlog (the Inbox unread badge covers catch-up).
 */
export function useReplyNotifications(enabled) {
  const sinceRef = useRef(null);
  const seenRef = useRef(new Set());

  useEffect(() => {
    if (!enabled) return undefined;

    // Ask for desktop-notification permission once (best-effort).
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    } catch { /* ignore */ }

    let cancelled = false;
    let timer = null;

    const openInbox = () => {
      window.dispatchEvent(new CustomEvent('ai-agent:open', { detail: { tab: 'email', emailTab: 'inbox' } }));
    };

    const announce = (ev) => {
      const vendor = ev.reply_to || 'A vendor';
      const subject = ev.subject || '(no subject)';
      const tag = ev.chat_title ? ` · ${ev.chat_title}` : '';
      toast.success(`New reply from ${vendor}`, {
        description: `${subject}${tag}`,
        action: { label: 'Open inbox', onClick: openInbox },
        duration: 8000,
      });
      try {
        if ('Notification' in window && Notification.permission === 'granted') {
          const n = new Notification(`New reply from ${vendor}`, {
            body: subject,
            tag: ev.id,
            icon: '/favicon.ico',
          });
          n.onclick = () => { window.focus(); openInbox(); n.close(); };
        }
      } catch { /* ignore */ }
    };

    const poll = async () => {
      try {
        const r = await aiAgentApi.replyEvents(sinceRef.current || '');
        if (cancelled) return;
        const first = sinceRef.current === null;
        sinceRef.current = r.server_time || sinceRef.current;
        // On the very first poll just establish the baseline — don't replay.
        if (!first) {
          const events = (r.events || []).filter((e) => e.id && !seenRef.current.has(e.id));
          // Oldest first so toasts stack in natural order.
          events.reverse().forEach((e) => { seenRef.current.add(e.id); announce(e); });
        } else {
          (r.events || []).forEach((e) => e.id && seenRef.current.add(e.id));
        }
      } catch { /* non-fatal */ }
    };

    poll();
    timer = setInterval(poll, POLL_MS);
    const onFocus = () => poll();
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled]);
}

export default useReplyNotifications;
